/**
 * Social-privacy "levels" — web's mirror of the mobile app's Full / Limited /
 * Minimum visibility model.
 *
 * "Level" is purely client-derived: the backend stores only per-key booleans on
 * `SocialPreferencesVo` (POST `/users/{id}/social/sharing_preference`), with no
 * server notion of a level. Mobile's level system touches exactly NINE managed
 * keys and writes them as a locked group; the other six keys
 * (`top_holders`, `metric_roi`, `metric_dollars_traded`,
 * `metric_dollars_investment`, `metric_portfolio_value`,
 * `metric_signed_open_position`) are "orphans" that no level reads or writes —
 * they are deliberately left untouched, matching mobile.
 *
 * EXCEPTION: `metric_dollars_traded` gates the profile-header "Volume" stat but
 * was dropped from mobile's level sets, leaving it stuck false (Volume hidden).
 * Web force-writes it `true` on every level change (see `FORCE_ENABLED_KEYS`)
 * until mobile re-adds it to its level sets in a follow-up build. It is NOT part
 * of level *derivation* — the 9-key managed model is unchanged.
 */
import type { SocialPreferencesVo } from 'models';

export type SharingLevel = 'full' | 'limited' | 'minimum';

/** The nine keys mobile's level system manages. Orphan keys are excluded. */
export type ManagedSharingKey =
    | 'metric_num_markets_traded'
    | 'metric_volume'
    | 'metric_pnl'
    | 'metric_category_positive_pnl'
    | 'metric_category_pnl'
    | 'activity'
    | 'portfolio'
    | 'closed_portfolio'
    | 'metric_open_interest';

/**
 * Each level's *enabled* key set, mirroring mobile's `enabledMetrics`
 * (`sharing_preference_level.dart`). Note `full ⊃ limited ⊃ minimum`.
 *
 * ⚠️ `metric_volume` is intentionally OFF at Minimum: this mirrors mobile's code,
 * whose user-facing copy promises "total volumes" at Minimum yet whose key map
 * writes `metric_volume = false`. We replicate the code for data parity; the
 * intent-vs-code conflict is flagged for product to resolve with mobile.
 */
const LEVEL_ENABLED_KEYS: Record<SharingLevel, readonly ManagedSharingKey[]> = {
    full: [
        'metric_num_markets_traded',
        'metric_volume',
        'metric_pnl',
        'metric_category_positive_pnl',
        'metric_category_pnl',
        'activity',
        'portfolio',
        'closed_portfolio',
        'metric_open_interest',
    ],
    limited: [
        'metric_num_markets_traded',
        'metric_volume',
        'metric_pnl',
        'metric_category_positive_pnl',
        'metric_category_pnl',
    ],
    minimum: ['metric_num_markets_traded'],
};

/** All nine managed keys (== the Full enabled set). */
export const MANAGED_SHARING_KEYS: readonly ManagedSharingKey[] = LEVEL_ENABLED_KEYS.full;

/**
 * Write-only override for `metric_dollars_traded`: it gates the profile-header
 * Volume stat but was dropped from mobile's level sets, leaving it stuck false.
 * Web re-adds it as a Full-only write (true at Full, false at Limited/Minimum)
 * until mobile restores it to its level sets next build. It rides alongside the
 * managed-key writes but is excluded from level *derivation*, so the 9-key
 * managed model is unchanged.
 */
const VOLUME_WRITE_KEY = 'metric_dollars_traded' satisfies keyof SocialPreferencesVo;

/** Derivation checks levels highest-first and takes the first fully-satisfied set. */
const LEVELS_HIGH_TO_LOW: readonly SharingLevel[] = ['full', 'limited', 'minimum'];

/**
 * Full-overwrite target for a level: every managed key mapped to its boolean for
 * that level (enabled → `true`, otherwise `false`). Mirrors mobile, which POSTs
 * all nine managed keys to these values on every level change (not a delta).
 *
 * Plus `metric_dollars_traded` as a Full-only write (see `VOLUME_WRITE_KEY`).
 */
export function getSharingLevelTargets(
    level: SharingLevel,
): Record<ManagedSharingKey | typeof VOLUME_WRITE_KEY, boolean> {
    const enabled = new Set<ManagedSharingKey>(LEVEL_ENABLED_KEYS[level]);
    const targets = MANAGED_SHARING_KEYS.reduce(
        (acc, key) => {
            acc[key] = enabled.has(key);
            return acc;
        },
        {} as Record<ManagedSharingKey | typeof VOLUME_WRITE_KEY, boolean>,
    );
    targets[VOLUME_WRITE_KEY] = level === 'full';
    return targets;
}

/**
 * Derive the level from a stored preference map: the highest level whose enabled
 * keys are ALL true. Mirrors mobile (`social_profile.dart`) — it only checks that
 * enabled keys are true, so extra keys being on never disqualify a level, and an
 * all-false map floors to `minimum`.
 *
 * NOTE: mobile defaults a profile with *no stored prefs* to `limited`. Web's
 * default `SocialPreferencesVo` is all-false (→ `minimum` here), so the
 * "no prefs → limited" default is a load/migration concern for the caller, not
 * this pure function.
 */
export function deriveSharingLevel(prefs: SocialPreferencesVo): SharingLevel {
    const isSatisfied = (level: SharingLevel): boolean => LEVEL_ENABLED_KEYS[level].every((key) => prefs[key]);
    return LEVELS_HIGH_TO_LOW.find(isSatisfied) ?? 'minimum';
}

/** A level "enables" a metric when that key is in its enabled set (drives feature-join badges). */
export function levelEnablesMetric(level: SharingLevel, key: ManagedSharingKey): boolean {
    return getSharingLevelTargets(level)[key];
}

/** Render order for the selector: most-shared first. */
export const SHARING_LEVEL_ORDER: readonly SharingLevel[] = ['full', 'limited', 'minimum'];

export interface SharingLevelCopy {
    label: string;
    description: string;
}

/**
 * Two copy sets mirroring mobile's two surfaces (the strings differ between them):
 *  - `feature` — mobile's "Customize visibilities" bottom sheet (leaderboard/feature-join context)
 *  - `profile` — mobile's Edit-Profile / setup selector (the main privacy control)
 */
export const SHARING_LEVEL_COPY: Record<'feature' | 'profile', Record<SharingLevel, SharingLevelCopy>> = {
    feature: {
        full: { label: 'Full', description: 'Others see your complete profile' },
        limited: { label: 'Limited', description: "Others won't see your trade activities" },
        minimum: { label: 'Minimum', description: 'Others will only see your followers, posts, total volumes' },
    },
    profile: {
        full: { label: 'Full', description: 'Others can see your full profile' },
        limited: { label: 'Limited', description: 'Hide your trading activities' },
        minimum: { label: 'Minimum', description: 'Only followers, posts, and total volumes shown' },
    },
};
